import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useMembers, useNamespace } from '../lib/hooks'
import { api } from '../lib/api'

const ROLES = ['owner', 'admin', 'member', 'reader'] as const
type Role = typeof ROLES[number]

export function Members() {
  const { slug = '' } = useParams()
  const ns      = useNamespace(slug)
  const members = useMembers(slug)

  const [showInvite, setShowInvite] = useState(false)
  const [email, setEmail]           = useState('')
  const [role, setRole]             = useState<Role>('member')
  const [busy, setBusy]             = useState<string | null>(null)
  const [feedback, setFeedback]     = useState<{ ok: boolean; msg: string } | null>(null)

  const canAdmin = ['owner', 'admin'].includes(ns.data?.role ?? '')
  const myRole   = ns.data?.role ?? ''

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    setBusy('invite')
    setFeedback(null)
    try {
      await api.post(`/api/namespaces/${slug}/members`, { email, role })
      setFeedback({ ok: true, msg: `${email} added as ${role}.` })
      setEmail('')
      setShowInvite(false)
      members.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  async function handleRoleChange(userId: string, newRole: Role) {
    setBusy(userId)
    setFeedback(null)
    try {
      await api.patch(`/api/namespaces/${slug}/members/${userId}`, { role: newRole })
      members.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  async function handleRemove(userId: string, email: string) {
    if (!window.confirm(`Remove ${email} from this namespace?`)) return
    setBusy(userId)
    setFeedback(null)
    try {
      await api.delete(`/api/namespaces/${slug}/members/${userId}`)
      setFeedback({ ok: true, msg: `${email} removed.` })
      members.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Layout
      title="Members"
      actions={
        canAdmin && (
          <button className="btn btn-primary" onClick={() => setShowInvite((v) => !v)}>
            {showInvite ? 'Cancel' : '+ Add member'}
          </button>
        )
      }
    >
      {showInvite && (
        <div className="card fu" style={{ marginBottom: 24 }}>
          <div className="card-header"><div className="kicker">Add member</div></div>
          <div className="card-body">
            <form onSubmit={handleInvite} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'end' }}>
              <div>
                <label className="field-label">Email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="field-label">Role</label>
                <select
                  className="input"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                  style={{ minWidth: 110 }}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={busy === 'invite'}>
                {busy === 'invite' ? <span className="spinner" /> : 'Add'}
              </button>
            </form>
            {feedback && !feedback.ok && (
              <div className="feedback-error" style={{ marginTop: 12 }}>{feedback.msg}</div>
            )}
          </div>
        </div>
      )}

      {feedback?.ok && (
        <div className="feedback-success" style={{ marginBottom: 16 }}>{feedback.msg}</div>
      )}

      {members.loading && <div className="empty-state">Loading…</div>}
      {members.error && (
        <div className="empty-state" style={{ color: 'var(--red)' }}>{members.error}</div>
      )}

      {(members.data?.members.length ?? 0) > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Joined</th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {members.data!.members.map((m) => (
                <tr key={m.user_id}>
                  <td>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>{m.email}</span>
                  </td>
                  <td>
                    {canAdmin && m.role !== 'owner' ? (
                      <select
                        className="input"
                        value={m.role}
                        disabled={busy === m.user_id}
                        onChange={(e) => handleRoleChange(m.user_id, e.target.value as Role)}
                        style={{ padding: '2px 8px', fontSize: '0.78rem', minWidth: 100 }}
                      >
                        {ROLES.filter((r) => myRole === 'owner' || r !== 'owner').map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`badge ${m.role === 'owner' ? 'badge-accent' : 'badge-neutral'}`}>
                        {m.role}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>
                    {new Date(m.joined_at).toLocaleDateString()}
                  </td>
                  {canAdmin && (
                    <td>
                      {m.role !== 'owner' && (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={busy === m.user_id}
                          onClick={() => handleRemove(m.user_id, m.email)}
                        >
                          {busy === m.user_id ? <span className="spinner" /> : 'Remove'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!members.loading && members.data?.members.length === 0 && (
        <div className="empty-state fu">
          <div className="empty-icon">⬡</div>
          <div>No members yet.</div>
        </div>
      )}
    </Layout>
  )
}
